import React, { useCallback, useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import {
  ManagedUser,
  UsersListResponse,
  listUsers,
  approveUser,
  suspendUser,
  setUserRole,
} from "../services/usersApi";
import "./UsersAdmin.css";

type StatusFilter = "all" | "pending" | "active" | "suspended";

const STATUS_LABEL: Record<string, string> = {
  pending: "Pending",
  active: "Active",
  suspended: "Suspended",
};

const ROLE_LABEL: Record<string, string> = {
  user: "User",
  admin: "Admin",
  super_admin: "Super Admin",
};

const UsersAdmin: React.FC = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<ManagedUser[]>([]);
  const [counts, setCounts] = useState({ pending: 0, active: 0, suspended: 0 });
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [busyUserId, setBusyUserId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(
    async (targetPage: number, filter: StatusFilter) => {
      setIsLoading(true);
      setError(null);
      try {
        const data: UsersListResponse = await listUsers({
          page: targetPage,
          limit: 20,
          ...(filter !== "all" ? { status: filter } : {}),
        });
        setUsers(data.users);
        setCounts(data.counts);
        setTotalPages(data.pagination.total_pages || 1);
        setPage(targetPage);
      } catch (err: any) {
        setError(
          err?.response?.data?.message || err?.message || "Failed to load users"
        );
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    load(1, statusFilter);
  }, [load, statusFilter]);

  const runAction = async (
    target: ManagedUser,
    action: () => Promise<ManagedUser>
  ) => {
    setBusyUserId(target._id);
    setError(null);
    try {
      await action();
      await load(page, statusFilter);
    } catch (err: any) {
      setError(
        err?.response?.data?.message || err?.message || "Action failed"
      );
    } finally {
      setBusyUserId(null);
    }
  };

  const isSelf = (u: ManagedUser) => currentUser?._id === u._id;
  const isProtected = (u: ManagedUser) => u.role === "super_admin" || isSelf(u);

  return (
    <div className="users-admin">
      <div className="users-admin-header">
        <h1>User Management</h1>
        <p className="users-admin-subtitle">
          New accounts wait here until you approve them. Changes take effect on
          the user's next request.
        </p>
      </div>

      <div className="users-admin-tabs">
        {(
          [
            ["all", `All (${counts.pending + counts.active + counts.suspended})`],
            ["pending", `Pending (${counts.pending})`],
            ["active", `Active (${counts.active})`],
            ["suspended", `Suspended (${counts.suspended})`],
          ] as [StatusFilter, string][]
        ).map(([value, label]) => (
          <button
            key={value}
            className={`users-tab ${statusFilter === value ? "active" : ""} ${
              value === "pending" && counts.pending > 0 ? "attention" : ""
            }`}
            onClick={() => setStatusFilter(value)}
          >
            {label}
          </button>
        ))}
      </div>

      {error && <div className="users-admin-error">{error}</div>}

      <div className="users-admin-table-wrap">
        <table className="users-admin-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Status</th>
              <th>Registered</th>
              <th>Last Login</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u._id} className={`status-row-${u.status}`}>
                <td>
                  {u.firstName} {u.lastName}
                  {isSelf(u) && <span className="you-badge">you</span>}
                </td>
                <td>{u.email}</td>
                <td>
                  {u.role === "super_admin" || isSelf(u) ? (
                    ROLE_LABEL[u.role] || u.role
                  ) : (
                    <select
                      value={u.role}
                      disabled={busyUserId === u._id}
                      onChange={(e) =>
                        runAction(u, () =>
                          setUserRole(u._id, e.target.value as "user" | "admin")
                        )
                      }
                    >
                      <option value="user">User</option>
                      <option value="admin">Admin</option>
                    </select>
                  )}
                </td>
                <td>
                  <span className={`status-badge status-${u.status}`}>
                    {STATUS_LABEL[u.status] || u.status}
                  </span>
                </td>
                <td>{new Date(u.createdAt).toLocaleDateString()}</td>
                <td>
                  {u.lastLogin
                    ? new Date(u.lastLogin).toLocaleString()
                    : "Never"}
                </td>
                <td className="users-actions">
                  {!isProtected(u) && u.status !== "active" && (
                    <button
                      className="users-btn approve"
                      disabled={busyUserId === u._id}
                      onClick={() => runAction(u, () => approveUser(u._id))}
                    >
                      Approve
                    </button>
                  )}
                  {!isProtected(u) && u.status === "active" && (
                    <button
                      className="users-btn suspend"
                      disabled={busyUserId === u._id}
                      onClick={() => runAction(u, () => suspendUser(u._id))}
                    >
                      Suspend
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {!isLoading && users.length === 0 && (
              <tr>
                <td colSpan={7} className="users-empty">
                  No users in this view.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="users-admin-pager">
          <button
            disabled={page <= 1 || isLoading}
            onClick={() => load(page - 1, statusFilter)}
          >
            Previous
          </button>
          <span>
            Page {page} of {totalPages}
          </span>
          <button
            disabled={page >= totalPages || isLoading}
            onClick={() => load(page + 1, statusFilter)}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default UsersAdmin;
